/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 01-Aug-2026
 * @author      : Vijayakumar S, with Aravindan (Claude-assisted)
 * @since       : 1.5.0.0
 * Requirement# : Communication Management §9 — Provider Web
 * Purpose      : The one place loading / empty / error / forbidden are rendered, so
 *                they cannot be conflated by any screen in this feature.
 * -----------------------------------------------------------------------------
 * Revision History
 * -----------------------------------------------------------------------------
 * Requirement/
 * Issue/WorkItem |   DATE      |   AUTHOR       |   DESCRIPTION OF CHANGE
 * -----------------------------------------------------------------------------
 * §9             | 01-Aug-2026 | Vijayakumar S  | Initial creation
 * T81 dead code  | 10-Aug-2026 | Claude         | Removed `useAsyncResource`'s `setData` member.
 *                                                 Every one of the five call sites destructures
 *                                                 `{ state, reload }` only; the sole caller was the
 *                                                 hook's own test probe, which is removed with it.
 *                                                 `reload` (the supported way to refresh) is
 *                                                 unchanged.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { ApiResult } from './MessageTemplateService';

/**
 * EMPTY, LOADING AND ERROR ARE DIFFERENT ANSWERS TO DIFFERENT QUESTIONS.
 *
 * "There are no pending change requests" and "we could not find out whether there are
 * pending change requests" look identical if both render as a blank list — and the first
 * is actively harmful, because a provider who reads it closes the tab. This module makes
 * the distinction structural rather than a matter of care at each call site:
 *
 *   - the loader returns a tagged `AsyncState`, so there is no `[]` for a failure to
 *     decay into;
 *   - `AsyncBoundary` renders each tag with a different component, different wording and
 *     a different `data-testid`, so a test can assert WHICH state appeared;
 *   - the empty state is the ONLY one that says anything resembling "nothing here", and
 *     it is only reachable from a 200.
 */

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'unauthorized' }
  | { status: 'forbidden' }
  | { status: 'failed'; message: string; httpStatus: number | null };

/** Maps a transport result onto a render state. Only `ok` can ever become `ready`. */
export const toAsyncState = <T,>(result: ApiResult<T>): AsyncState<T> => {
  switch (result.kind) {
    case 'ok':
      return { status: 'ready', data: result.data };
    case 'unauthorized':
      return { status: 'unauthorized' };
    case 'forbidden':
      return { status: 'forbidden' };
    case 'notFound':
      return { status: 'failed', message: result.message, httpStatus: 404 };
    case 'rejected':
      return { status: 'failed', message: result.message, httpStatus: result.status };
    default:
      return { status: 'failed', message: result.message, httpStatus: result.status };
  }
};

/**
 * Loads once per `deps` change and exposes a reload. Guards against setting state after
 * unmount and against a slow first response overwriting a newer one.
 */
export function useAsyncResource<T>(
  load: (signalIsCurrent: () => boolean) => Promise<ApiResult<T>>,
  deps: React.DependencyList,
): { state: AsyncState<T>; reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const runIdRef = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    let cancelled = false;
    const isCurrent = () => !cancelled && runIdRef.current === runId;

    setState({ status: 'loading' });
    load(isCurrent)
      .then((result) => {
        if (isCurrent()) setState(toAsyncState(result));
      })
      .catch((thrown: any) => {
        // The service layer already tags transport failures; this is the last-resort net
        // for a genuinely unexpected throw, which must still not read as "empty".
        if (isCurrent()) {
          setState({
            status: 'failed',
            message: thrown?.message ?? 'Unexpected failure while loading',
            httpStatus: null,
          });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { state, reload };
}

export const LoadingState: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
  <Box
    data-testid="state-loading"
    role="status"
    aria-live="polite"
    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}
  >
    <CircularProgress size={22} />
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

/** The only "nothing here" message in the feature. Reachable only from a successful 200. */
export const EmptyState: React.FC<{ title: string; detail?: string }> = ({ title, detail }) => (
  <Box data-testid="state-empty" sx={{ py: 5, textAlign: 'center' }}>
    <Typography variant="subtitle1" color="text.secondary">
      {title}
    </Typography>
    {detail && (
      <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
        {detail}
      </Typography>
    )}
  </Box>
);

export const ErrorState: React.FC<{
  message: string;
  httpStatus: number | null;
  onRetry?: () => void;
}> = ({ message, httpStatus, onRetry }) => (
  <Alert
    data-testid="state-error"
    severity="error"
    sx={{ my: 2 }}
    action={
      onRetry && (
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      )
    }
  >
    <AlertTitle>Could not load — this is not an empty list</AlertTitle>
    {message}
    {httpStatus != null && ` (HTTP ${httpStatus})`}
  </Alert>
);

export const ForbiddenState: React.FC<{ what: string }> = ({ what }) => (
  <Alert data-testid="state-forbidden" severity="warning" sx={{ my: 2 }}>
    <AlertTitle>You do not have provider-admin access</AlertTitle>
    {what} requires <strong>provider-admin</strong> permission, which your account does not have.
    Your sign-in is valid, but this request was refused — so this is <strong>not</strong> a
    statement that there is nothing here.
  </Alert>
);

export const UnauthorizedState: React.FC = () => (
  <Alert data-testid="state-unauthorized" severity="warning" sx={{ my: 2 }}>
    <AlertTitle>Your session is not valid</AlertTitle>
    Sign in again to continue. Nothing was loaded, and nothing here reflects the real queue.
  </Alert>
);

/**
 * Renders the four non-ready states uniformly and hands `ready` data to `children`.
 * `isEmpty` is consulted ONLY once data has actually arrived.
 */
export function AsyncBoundary<T>(props: {
  state: AsyncState<T>;
  what: string;
  onRetry?: () => void;
  loadingLabel?: string;
  isEmpty?: (data: T) => boolean;
  emptyTitle?: string;
  emptyDetail?: string;
  children: (data: T) => React.ReactNode;
}): React.ReactElement {
  const { state, what, onRetry, loadingLabel, isEmpty, emptyTitle, emptyDetail, children } = props;

  // Written as an if-chain rather than a switch so TypeScript narrows `state` to the
  // `ready` variant below without relying on control-flow analysis through a default case.
  if (state.status === 'loading') {
    return <LoadingState label={loadingLabel} />;
  }
  if (state.status === 'unauthorized') {
    return <UnauthorizedState />;
  }
  if (state.status === 'forbidden') {
    return <ForbiddenState what={what} />;
  }
  if (state.status === 'failed') {
    return <ErrorState message={state.message} httpStatus={state.httpStatus} onRetry={onRetry} />;
  }

  if (isEmpty && isEmpty(state.data)) {
    return <EmptyState title={emptyTitle ?? 'Nothing here yet'} detail={emptyDetail} />;
  }
  return <>{children(state.data)}</>;
}
